/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import produce from 'immer';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type {
  CreateSavedSearchApiResponse,
  DeleteSavedSearchApiResponse,
  ReadAdvancedSearchResultsApiResponse,
  ReadSavedSearchApiResponse,
  ReadSavedSearchListApiResponse,
  ReadSearchEnrichedFiltersApiResponse,
  SavedSearch,
  SearchFilters,
  UpdateSavedSearchApiResponse,
} from 'lib-shared-types-api';

import { filtersInitialState } from 'app/views/layout/views/search/components/filters/utils/definitions';
import { enrichStaticFilters } from 'app/views/layout/views/search/utils/filters/enrichStaticFilters';

/**
 * Transforms the read advanced search results endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadAdvancedSearchResultsResponse(
  response: any,
): ReadAdvancedSearchResultsApiResponse {
  transformSearchResultsAttributes(response);

  return {
    data: {
      results: response.data.map((result) => ({
        ...result,
        nb_pages: result.nb_pages,
        // Pagination is 1-based in the frontend and 0-based in the backend
        page: result.page + 1,
      })),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the read saved search list endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadSavedSearchListResponse(
  response: any,
): ReadSavedSearchListApiResponse {
  const data = response.data.saved_searches ?? response.data;

  return {
    data: {
      saved_searches: data.map(transformSavedSearch),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the create saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformCreateSavedSearchResponse(
  response: any,
): CreateSavedSearchApiResponse {
  return {
    data: {
      saved_search: transformSavedSearch(
        response.data.saved_search ?? response.data,
      ),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the read saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadSavedSearchResponse(
  response: any,
): ReadSavedSearchApiResponse {
  return {
    data: {
      saved_search: transformSavedSearch(
        response.data.saved_search ?? response.data,
      ),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the update saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformUpdateSavedSearchResponse(
  response: any,
): UpdateSavedSearchApiResponse {
  return {
    data: {
      saved_search: transformSavedSearch(
        response.data.saved_search ?? response.data,
      ),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the delete saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformDeleteSavedSearchResponse(
  response: any,
): DeleteSavedSearchApiResponse {
  return {
    data: {
      id: response.data,
    },
    meta: response.meta,
  };
}

export function transformReadSearchEnrichedFiltersResponse(
  response: any,
): ReadSearchEnrichedFiltersApiResponse {
  return {
    data: {
      filters: response.data.params.filters,
    },
    meta: response.meta,
  };
}

function transformSavedSearch(savedSearch: any): SavedSearch {
  return produce<SavedSearch>(savedSearch, (draft) => {
    draft.params.query = draft.params.query ?? '';

    draft.params.filters = (
      draft.params.filters
        ? enrichStaticFilters(draft.params.filters)
        : filtersInitialState
    ) as SearchFilters;
  });
}

/**
 * Transforms the search results attributes from a raw response.
 *
 * @param response The raw response.
 */
function transformSearchResultsAttributes(response: any) {
  response.data.forEach((hitsType) =>
    hitsType.hits.forEach((hit) => {
      if (typeof hit.initial_channel !== 'undefined') {
        hit.initial_channel = convertChannelCasing(
          hit.initial_channel,
          'snake',
        );
      }

      if (typeof hit.inboundmessagecount === 'string') {
        hit.inboundmessagecount = JSON.parse(hit.inboundmessagecount);
      }

      if (typeof hit.outboundmessagecount === 'string') {
        hit.outboundmessagecount = JSON.parse(hit.outboundmessagecount);
      }

      delete hit.document_type;
      delete hit.join_field;
    }),
  );
}
